import { useCallback, useMemo } from 'react';
import {
    DOI_ERROR_CODE,
    DOI_URL,
    EMAIL_ERROR_CODE,
    INTERNAL_ERROR_CODE,
    PASSWORD_ERROR_CODE,
    USER_LOGGED_IN_ERROR_CODE,
} from 'SharedHelpers/LoginRegisterFormAPI';
import { isHostnameError } from 'SharedHelpers/error';
import useTranslate from 'Hooks/useTranslate';
import useLocationChange from 'Hooks/useLocationChange';
import { FormFieldTracking } from 'Interfaces/overlayTracking';
import useSignupFormTracking from 'Hooks/useSignupFormTracking';
import useLoginFormTracking from 'Hooks/useLoginFormTracking';

interface SubmitInfoInterface {
    isLastStep: boolean;
    isMultiStep: boolean;
    redirectUrl: string;
    showLogin: boolean;
}

interface CallbacksInterface {
    setError: Function;
    setIsLoading: Function;
}

interface ErrorHandlerHook {
    errorHandler: (submitInfo: SubmitInfoInterface, data: any, callbacks: CallbacksInterface) => void;
}

export const useClubErrorHandler = (): ErrorHandlerHook => {
    const { t, tPlain } = useTranslate();
    const DEFAULT_ERROR_MSG: { [key: string]: string } = {
        email: tPlain('signupOverlay_Please enter a valid email address'),
        password: tPlain('signupOverlay_Invalid password'),
    };

    const GENERIC_ERROR_MSG = t('signupOverlay_generic error message');
    const DEFAULT_REDIRECT_URL = '/c/campaign/';
    const ERROR_TYPE_CUSTOM = 'custom';
    const ERROR_TYPE_UNKNOWN = 'UnknownLoginSignupErr';

    const { locationChange } = useLocationChange();
    const { trackFieldError: trackSignupFieldError } = useSignupFormTracking();
    const { trackFieldError: trackLoginFieldError } = useLoginFormTracking();

    const trackFieldErrors = (code: number, showLogin: boolean) => {
        const trackErrorEvent = showLogin ? trackLoginFieldError : trackSignupFieldError;
        if (code === PASSWORD_ERROR_CODE) {
            trackErrorEvent(FormFieldTracking.PasswordFormField);
        }
        if (code === EMAIL_ERROR_CODE) {
            trackErrorEvent(FormFieldTracking.EmailFormField);
        }
    };

    const handleDoiError = useCallback(() => {
        locationChange(DOI_URL);
    }, [locationChange]);

    const errorCodeHandlers: Record<number, (submitInfo: SubmitInfoInterface, callbacks: CallbacksInterface) => void> =
        useMemo(
            () => ({
                [DOI_ERROR_CODE]: () => handleDoiError(),
                [INTERNAL_ERROR_CODE]: (submitInfo, callbacks) => {
                    callbacks.setIsLoading(false);
                    const errorField = submitInfo.isMultiStep && submitInfo.isLastStep ? 'subscribe' : 'email';
                    callbacks.setError(errorField, {
                        type: 'custom',
                        message: GENERIC_ERROR_MSG,
                    });
                    return undefined;
                },
                [USER_LOGGED_IN_ERROR_CODE]: submitInfo => {
                    locationChange(submitInfo.redirectUrl || DEFAULT_REDIRECT_URL);
                },
            }),
            [handleDoiError, GENERIC_ERROR_MSG, locationChange]
        );

    const errorHandler = (submitInfo: SubmitInfoInterface, data: any, callbacks: CallbacksInterface) => {
        const { setError, setIsLoading } = callbacks;
        const { showLogin } = submitInfo;

        try {
            // eslint-disable-next-line camelcase
            const { errors = { validation_errors: [], code: 0 } } = data;
            if (errors?.code) {
                const errorCodeHandler = errorCodeHandlers[errors.code];

                if (errorCodeHandler) {
                    return errorCodeHandler(submitInfo, callbacks);
                }

                trackFieldErrors(errors.code, showLogin);
            }

            const { data: errorBag } = errors;
            if (errorBag !== null && typeof errorBag === 'object' && Object.keys(errorBag).length) {
                Object.keys(errorBag).forEach(key =>
                    setError(key, {
                        type: ERROR_TYPE_CUSTOM,
                        message: isHostnameError(errorBag[key][0])
                            ? GENERIC_ERROR_MSG
                            : errorBag[key][0] || DEFAULT_ERROR_MSG[key],
                    })
                );
                setIsLoading(false);
                return undefined;
            }

            const unexpectedErr = new Error(JSON.stringify(data));
            unexpectedErr.name = ERROR_TYPE_UNKNOWN;
            throw unexpectedErr;
        } catch (e) {
            setError('email', {
                type: ERROR_TYPE_CUSTOM,
                message: GENERIC_ERROR_MSG,
            });
        }
        return undefined;
    };

    return {
        errorHandler,
    };
};
